class TpCompreJunto extends HTMLElement {
  connectedCallback() {
    this._cards = [];
    this._btn = this.querySelector('.tp-cj__add-btn');
    this._totalPriceEl = this.querySelector('.tp-cj__total-price');
    this._totalCompareEl = this.querySelector('.tp-cj__total-compare');
    this._totalBadgeEl = this.querySelector('.tp-cj__total-badge');
    this._cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    this.querySelectorAll('.tp-cj__card').forEach((cardEl) => this._initCard(cardEl));
    this._updateTotal();
    this._btn?.addEventListener('click', () => this._addToCart());
  }

  disconnectedCallback() {
    this._cards.forEach((card) => this._destroySwiper(card));
  }

  // ── Card init ───────────────────────────────────────────────

  _initCard(cardEl) {
    const data = JSON.parse(cardEl.querySelector('.tp-cj__card-data').textContent);
    const firstColor = data.colorValuesCount > 1
      ? cardEl.querySelector('.tp-cj__swatch--active')?.dataset.color ?? null
      : null;
    const firstAvail = data.variants.find((v) => v.available) || data.variants[0];

    const card = { el: cardEl, data, _swiper: null, selectedVariantId: firstAvail?.id ?? null };
    this._cards.push(card);

    this._buildSwiper(card, firstColor);
    this._initSwatches(card);
    this._initPills(card);
    this._initSelect(card);

    if (firstAvail) this._updateCardPrice(card, firstAvail);
  }

  // ── Swiper ──────────────────────────────────────────────────

  _destroySwiper(card) {
    if (card._swiper) {
      card._swiper.destroy(true, true);
      card._swiper = null;
    }
  }

  _getImagesForColor(card, color) {
    const images = card.data.images || [];
    if (!color || card.data.colorValuesCount <= 1) return images;
    const colorLower = color.toLowerCase();
    const filtered = images.filter((img) => img.alt && img.alt.toLowerCase() === colorLower);
    return filtered.length > 0 ? filtered : images;
  }

  _buildSwiper(card, color) {
    this._destroySwiper(card);

    const wrapper = card.el.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    const images = this._getImagesForColor(card, color);

    if (images.length === 0) {
      wrapper.innerHTML = '<div class="swiper-slide"><div class="tp-cj__img-placeholder"></div></div>';
    } else {
      wrapper.innerHTML = images
        .map(
          (img) =>
            `<div class="swiper-slide"><img class="tp-cj__img" src="${img.src}" alt="${img.alt || ''}" width="${img.width}" height="${img.height}" loading="lazy"></div>`
        )
        .join('');
    }

    const prevEl = card.el.querySelector('.tp-cj__nav--prev');
    const nextEl = card.el.querySelector('.tp-cj__nav--next');
    const multi = images.length > 1;

    if (prevEl) prevEl.style.display = multi ? '' : 'none';
    if (nextEl) nextEl.style.display = multi ? '' : 'none';

    if (typeof Swiper === 'undefined') {
      window.addEventListener('load', () => this._buildSwiper(card, color), { once: true });
      return;
    }

    card._swiper = new Swiper(card.el.querySelector('.tp-cj__swiper'), {
      loop: multi,
      navigation: multi && prevEl && nextEl ? { prevEl, nextEl } : false,
    });
  }

  // ── Swatch / pill / select ───────────────────────────────────

  _initSwatches(card) {
    card.el.querySelectorAll('.tp-cj__swatch').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('tp-cj__swatch--active')) return;
        card.el.querySelectorAll('.tp-cj__swatch').forEach((b) => b.classList.remove('tp-cj__swatch--active'));
        btn.classList.add('tp-cj__swatch--active');
        this._onColorChange(card, btn.dataset.color);
      });
    });
  }

  _initPills(card) {
    card.el.querySelectorAll('.tp-cj__pill').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        card.el.querySelectorAll('.tp-cj__pill').forEach((b) => b.classList.remove('tp-cj__pill--active'));
        btn.classList.add('tp-cj__pill--active');
        this._resolveVariant(card);
      });
    });
  }

  _initSelect(card) {
    const select = card.el.querySelector('.tp-cj__select');
    if (!select) return;
    select.addEventListener('change', () => {
      const varId = parseInt(select.value, 10);
      card.selectedVariantId = varId;
      const v = card.data.variants.find((v) => v.id === varId);
      if (v) this._updateCardPrice(card, v);
      this._updateTotal();
    });
  }

  _onColorChange(card, color) {
    this._refreshPillAvailability(card, color);
    this._buildSwiper(card, color);
    this._resolveVariant(card);
  }

  _resolveVariant(card) {
    const { data } = card;
    const color = card.el.querySelector('.tp-cj__swatch--active')?.dataset.color ?? null;
    const size = card.el.querySelector('.tp-cj__pill--active')?.dataset.size ?? null;

    let variant =
      data.variants.find((v) => {
        const colorOk = data.colorOptionIndex < 0 || data.colorValuesCount <= 1 || !color || v.options[data.colorOptionIndex] === color;
        const sizeOk = data.sizeOptionIndex < 0 || !size || v.options[data.sizeOptionIndex] === size;
        return colorOk && sizeOk && v.available;
      }) ||
      data.variants.find((v) => {
        const colorOk = data.colorOptionIndex < 0 || data.colorValuesCount <= 1 || !color || v.options[data.colorOptionIndex] === color;
        return colorOk && v.available;
      }) ||
      data.variants.find((v) => v.available) ||
      data.variants[0];

    if (!variant) return;

    card.selectedVariantId = variant.id;
    this._updateCardPrice(card, variant);
    this._updateTotal();

    // Sync active pill to resolved size
    if (data.sizeOptionIndex >= 0 && variant.options[data.sizeOptionIndex]) {
      const resolvedSize = variant.options[data.sizeOptionIndex];
      card.el.querySelectorAll('.tp-cj__pill').forEach((btn) => {
        btn.classList.toggle('tp-cj__pill--active', btn.dataset.size === resolvedSize);
      });
    }

    // Sync mobile select
    const select = card.el.querySelector('.tp-cj__select');
    if (select) select.value = String(variant.id);
  }

  _refreshPillAvailability(card, color) {
    const { data } = card;
    if (data.sizeOptionIndex < 0) return;
    card.el.querySelectorAll('.tp-cj__pill').forEach((btn) => {
      const size = btn.dataset.size;
      const avail = data.variants.some(
        (v) =>
          v.options[data.sizeOptionIndex] === size &&
          (data.colorOptionIndex < 0 || data.colorValuesCount <= 1 || v.options[data.colorOptionIndex] === color) &&
          v.available
      );
      btn.classList.toggle('tp-cj__pill--unavailable', !avail);
      btn.disabled = !avail;
    });
  }

  // ── Price / total ────────────────────────────────────────────

  _updateCardPrice(card, variant) {
    const priceEl = card.el.querySelector('.tp-cj__price-current');
    const compareEl = card.el.querySelector('.tp-cj__price-compare');
    if (priceEl) priceEl.textContent = this._fmt(variant.price);
    if (compareEl) {
      const hasCompare = variant.compareAtPrice > variant.price;
      compareEl.hidden = !hasCompare;
      if (hasCompare) compareEl.textContent = this._fmt(variant.compareAtPrice);
    }
  }

  _updateTotal() {
    let total = 0;
    let compare = 0;
    this._cards.forEach((card) => {
      const v = card.data.variants.find((v) => v.id === card.selectedVariantId);
      if (!v) return;
      total += v.price;
      compare += v.compareAtPrice > v.price ? v.compareAtPrice : v.price;
    });

    if (this._totalPriceEl) this._totalPriceEl.textContent = this._fmt(total);

    if (compare > total) {
      if (this._totalCompareEl) { this._totalCompareEl.hidden = false; this._totalCompareEl.textContent = this._fmt(compare); }
      if (this._totalBadgeEl) { this._totalBadgeEl.hidden = false; this._totalBadgeEl.textContent = `-${Math.round(((compare - total) / compare) * 100)}%`; }
    } else {
      if (this._totalCompareEl) this._totalCompareEl.hidden = true;
      if (this._totalBadgeEl) this._totalBadgeEl.hidden = true;
    }
  }

  // ── Add to cart ──────────────────────────────────────────────

  async _addToCart() {
    const items = this._cards
      .filter((c) => c.selectedVariantId)
      .map((c) => ({ id: c.selectedVariantId, quantity: 1 }));
    if (!items.length) return;

    const btn = this._btn;
    const originalText = btn.dataset.originalText || btn.textContent.trim();
    btn.disabled = true;
    btn.classList.add('is-loading');

    const sectionsToRender = this._cartEl?.getSectionsToRender?.().map((s) => s.id) ?? [];

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionsToRender, sections_url: window.location.pathname }),
      });

      const response = await res.json();

      if (response.status) throw new Error(response.description || 'Erro ao adicionar');

      // 1. Pub/sub para subscribers (cart icon bubble, etc.) — mesmo order que product-form.js
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tp-compre-junto', cartData: response });
      }

      // 2. Renderiza e abre o cart drawer/notification
      if (this._cartEl?.renderContents) {
        this._cartEl.renderContents(response);
        if (this._cartEl.classList.contains('is-empty')) this._cartEl.classList.remove('is-empty');
      } else {
        window.location.href = '/cart';
        return;
      }

      btn.classList.remove('is-loading');
      btn.classList.add('is-success');
      btn.textContent = 'Adicionado! ✓';

      setTimeout(() => {
        btn.classList.remove('is-success');
        btn.textContent = originalText;
        btn.disabled = false;
      }, 2500);
    } catch (err) {
      console.error('[tp-compre-junto] cart error:', err);
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  }

  _fmt(cents) {
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
}

customElements.define('tp-compre-junto', TpCompreJunto);
